import {useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {SalesCompare} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {ColumnFlexBox, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox, HighlightBox, SectionBlock, SectionSubTitle, SectionTitle
} from '../../StyledComponents';
import {AgeGenderRadarChart} from '../Chart/AgeGenderRadarChart';
import {AGE_GENDER_SUBJECTS, EMPTY_ITEM_TYPES} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {SalesDataProps} from '../interface';

export const SalesAgeGenderCompare = ({
  isPending,
  error,
  data: rawdata,
}: SalesDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return rawdata?.map((i, idx) => ({
      ...(i?.ageGender || {}),
      idx,
    }));
  }, [rawdata]);

  return (
    <SectionBlock>
      <SectionTitle>결제 비중(성연령별)</SectionTitle>
      <br />
      <SectionSubTitle>{`${metadata?.source || '카드사'}∙${
        metadata?.baseDate || ''
      }`}</SectionSubTitle>
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Sales} />
        ) : (
          <SalesAgeGenderChartBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const SalesAgeGenderChartBox = ({
  items,
}: {
  items: Array<SalesCompare['ageGender']>;
}) => {
  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const maxDataIndex = items.map((item) => {
    const {maleAgeGender, femaleAgeGender} = item;
    const maxMaleAge = Math.max(...maleAgeGender.map((item) => item));
    const maxFemaleAge = Math.max(...femaleAgeGender.map((item) => item));
    const maxGender =
      maxMaleAge > maxFemaleAge ? 'maleAgeGender' : 'femaleAgeGender';
    const maxAge = maxMaleAge > maxFemaleAge ? maxMaleAge : maxFemaleAge;
    const maxIndex = item[maxGender].findIndex((item) => item === maxAge);
    return {maxIndex, maxAge, maxGender};
  });

  const makeMaxPerIndex = (index: number) => {
    const genderName = [
      maxDataIndex[index].maxGender === 'maleAgeGender' ? '남성' : '여성',
    ];
    const maxIndex = maxDataIndex[index].maxIndex;
    if (maxIndex < 0) return null;
    return `${AGE_GENDER_SUBJECTS[maxDataIndex[index].maxIndex]} ${genderName}`;
  };

  return (
    <ColumnFlexBox gap={16}>
      {items.map((item, index) => (
        <AgeGenderRadarChart
          key={`성연령별결제비중-${index}`}
          type={'성연령별 결제비중'}
          data={item}
          compareItemName={compareList[index]}
          compareItemIndex={index}
        />
      ))}
      <HighlightBox>
        {!!compareList.length && (
          <>
            {compareList.map((item, index) => {
              const maxPerIndex = makeMaxPerIndex(index);

              return (
                <Typography
                  variant="medium12"
                  as="div"
                  key={`SalesAgeGender-CompareList-${index}`}
                >
                  <Typography color={palette.primary500} fontWeight="700">
                    {item}
                  </Typography>
                  은/는{' '}
                  {!!maxPerIndex ? (
                    <>
                      <strong>{maxPerIndex}</strong>의 결제 비중이 가장 높아요.
                    </>
                  ) : (
                    '정보가 없어요.'
                  )}
                </Typography>
              );
            })}
          </>
        )}
      </HighlightBox>
    </ColumnFlexBox>
  );
};
